/**
 * Deterministic mock data for the time-tracking redesign.
 *
 * Activated when `VITE_TIME_TRACKING_MOCK=true`. Returns the **domain models**
 * (hours, not minutes) directly so the api layer can short-circuit without
 * touching axios or the mappers.
 *
 * Once the backend ships the new endpoints (see OPS-1385), this file can be
 * deleted along with the env flag.
 */
import { addDays, isWeekend, parseISO } from 'date-fns';

import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { calculateProgressPercentage } from '../../helpers/progress-percentage.helper';
import {
  DayOverview,
  MonthCalendarDay,
  MonthOverview,
  ProjectTimeTrackingSummary,
  TimeOffRequestSummaryStatus,
  TimeTrack,
  WeekDayBreakdown,
  WeekDayProjectBreakdown,
  WeeklySummary,
} from '../../types/time-tracking';

interface MockProject {
  projectId: number;
  projectName: string;
  colorHex: string;
  logoUrl: string | null;
  dailyBookedHours: number;
}

const MOCK_PROJECTS: MockProject[] = [
  {
    projectId: 101,
    projectName: 'Northwave website',
    colorHex: '#E76E50',
    logoUrl: null,
    dailyBookedHours: 4,
  },
  {
    projectId: 102,
    projectName: 'Compass mobile app',
    colorHex: '#2A9D90',
    logoUrl: null,
    dailyBookedHours: 2,
  },
  {
    projectId: 103,
    projectName: 'Helio data platform',
    colorHex: '#8B5CF6',
    logoUrl: null,
    dailyBookedHours: 2,
  },
  {
    projectId: 104,
    projectName: 'Internal · Operations',
    colorHex: '#71717A',
    logoUrl: null,
    dailyBookedHours: 0,
  },
];

const DAILY_CAPACITY_HOURS = 8;

const isMockEnabled = (): boolean =>
  import.meta.env.VITE_TIME_TRACKING_MOCK === 'true' ||
  import.meta.env.VITE_TIME_TRACKING_MOCK === '1';

/**
 * Day scenarios to cover all UnifiedSummary states. Each weekday of the month
 * (1..31) maps deterministically to one of these so the user can see all
 * states by scrolling a single month.
 *
 * Capacity is 8h per weekday. Each scenario describes how to fill that 8h
 * with tracked + booked + time-off across the four mock projects.
 */
type DayScenarioId =
  | 'normal-under'
  | 'normal-on-target'
  | 'todo-only'
  | 'overbook-no-overtrack'
  | 'overtrack-no-overbook'
  | 'overtrack-and-overbook'
  | 'available-only-no-booked'
  | 'timeoff-approved-half'
  | 'timeoff-pending-half'
  | 'timeoff-approved-and-pending'
  | 'timeoff-approved-full'
  | 'timeoff-pending-full'
  | 'timeoff-multi-stacked'
  | 'bg-work-half'
  | 'bg-overtrack';

interface DayScenarioTimeOff {
  status: TimeOffRequestSummaryStatus;
  hours: number;
}

interface DayScenarioProject {
  tracked: number;
  booked: number;
  /**
   * When true, the project's booked time on this day is a background-work
   * assignment and its tracked time is split into a background bucket too.
   * `tracked` and `booked` remain the full amounts; the mock derives
   * regular vs background slices from this flag.
   */
  isBackgroundWork?: boolean;
}

interface DayScenario {
  /** Time-off entries for this day. May contain at most one APPROVED + one PENDING. */
  timeOff?: DayScenarioTimeOff[];
  /** Per-project (tracked, booked) overrides indexed by mock project order. */
  perProject: DayScenarioProject[];
}

/**
 * Build a scenario definition. `perProject` aligns to MOCK_PROJECTS by index.
 * Indexes: 0 Northwave (booked 4h), 1 Compass (2h), 2 Helio (2h), 3 Internal (0h).
 */
const SCENARIOS: Record<DayScenarioId, DayScenario> = {
  // 8h booked, fully tracked. Should be all green, no cap line.
  'normal-on-target': {
    perProject: [
      { tracked: 4, booked: 4 },
      { tracked: 2, booked: 2 },
      { tracked: 2, booked: 2 },
      { tracked: 0, booked: 0 },
    ],
  },
  // 8h booked, only 5h tracked. Should show green + striped left-to-log.
  'normal-under': {
    perProject: [
      { tracked: 2.5, booked: 4 },
      { tracked: 1, booked: 2 },
      { tracked: 1.5, booked: 2 },
      { tracked: 0, booked: 0 },
    ],
  },
  // 6h booked, 0 tracked. Pure striped + a bit of available.
  'todo-only': {
    perProject: [
      { tracked: 0, booked: 3 },
      { tracked: 0, booked: 2 },
      { tracked: 0, booked: 1 },
      { tracked: 0, booked: 0 },
    ],
  },
  // 11h booked, 6h tracked → overbook but not overtrack. Should show purple-striped.
  'overbook-no-overtrack': {
    perProject: [
      { tracked: 3, booked: 5 },
      { tracked: 2, booked: 3 },
      { tracked: 1, booked: 3 },
      { tracked: 0, booked: 0 },
    ],
  },
  // 8h booked, 10.5h tracked → overtrack only. Should show purple-solid for excess.
  'overtrack-no-overbook': {
    perProject: [
      { tracked: 5, booked: 4 },
      { tracked: 3, booked: 2 },
      { tracked: 2.5, booked: 2 },
      { tracked: 0, booked: 0 },
    ],
  },
  // 11h booked, 13h tracked → overtrack AND overbook (both overflows visible).
  'overtrack-and-overbook': {
    perProject: [
      { tracked: 6, booked: 5 },
      { tracked: 4, booked: 3 },
      { tracked: 3, booked: 3 },
      { tracked: 0, booked: 0 },
    ],
  },
  // Nothing booked, nothing tracked → "available" fills the whole work zone.
  'available-only-no-booked': {
    perProject: [
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
    ],
  },
  // 4h approved time-off + 4h booked, 4h tracked. Half day off.
  'timeoff-approved-half': {
    timeOff: [{ status: TimeOffRequestSummaryStatus.APPROVED, hours: 4 }],
    perProject: [
      { tracked: 2, booked: 2 },
      { tracked: 1, booked: 1 },
      { tracked: 1, booked: 1 },
      { tracked: 0, booked: 0 },
    ],
  },
  // 4h pending time-off + 4h booked, 4h tracked.
  'timeoff-pending-half': {
    timeOff: [{ status: TimeOffRequestSummaryStatus.PENDING, hours: 4 }],
    perProject: [
      { tracked: 2, booked: 2 },
      { tracked: 1, booked: 1 },
      { tracked: 1, booked: 1 },
      { tracked: 0, booked: 0 },
    ],
  },
  // 2h approved + 4h pending on the same day. Capacity reduced only by the approved 2h.
  'timeoff-approved-and-pending': {
    timeOff: [
      { status: TimeOffRequestSummaryStatus.APPROVED, hours: 2 },
      { status: TimeOffRequestSummaryStatus.PENDING, hours: 4 },
    ],
    perProject: [
      { tracked: 1, booked: 1 },
      { tracked: 1, booked: 1 },
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
    ],
  },
  // Full day approved time-off. No tracked, no booked.
  'timeoff-approved-full': {
    timeOff: [{ status: TimeOffRequestSummaryStatus.APPROVED, hours: 8 }],
    perProject: [
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
    ],
  },
  // Full day pending time-off. No tracked, no booked.
  'timeoff-pending-full': {
    timeOff: [{ status: TimeOffRequestSummaryStatus.PENDING, hours: 8 }],
    perProject: [
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
    ],
  },
  // 2h pending + 4h pending + 2h approved — three stacked time-off segments on one day.
  // The two pending entries arrive as separate items so the UI can stack them.
  'timeoff-multi-stacked': {
    timeOff: [
      { status: TimeOffRequestSummaryStatus.PENDING, hours: 6 },
      { status: TimeOffRequestSummaryStatus.APPROVED, hours: 2 },
    ],
    perProject: [
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
      { tracked: 0, booked: 0 },
    ],
  },
  // Half-day of background work. Internal project (idx 3) is background.
  // 4h regular tracked across Northwave + Compass + Helio, 2h tracked on Internal (BG).
  'bg-work-half': {
    perProject: [
      { tracked: 2, booked: 2 },
      { tracked: 1, booked: 1 },
      { tracked: 1, booked: 1 },
      { tracked: 2, booked: 2, isBackgroundWork: true },
    ],
  },
  // Background work tracked over capacity.
  // 4h regular + 6h background tracked = 10h over an 8h cap.
  'bg-overtrack': {
    perProject: [
      { tracked: 2, booked: 2 },
      { tracked: 1, booked: 1 },
      { tracked: 1, booked: 1 },
      { tracked: 6, booked: 4, isBackgroundWork: true },
    ],
  },
};

/**
 * Map each weekday of the month to a scenario id. Cycle through scenarios
 * across weekdays so any visible week / month surfaces them all.
 */
const SCENARIO_ROTATION: DayScenarioId[] = [
  'normal-on-target',
  'normal-under',
  'overbook-no-overtrack',
  'bg-work-half',
  'overtrack-no-overbook',
  'overtrack-and-overbook',
  'bg-overtrack',
  'todo-only',
  'available-only-no-booked',
  'timeoff-approved-half',
  'timeoff-pending-half',
  'timeoff-multi-stacked',
  'timeoff-approved-and-pending',
  'timeoff-approved-full',
  'timeoff-pending-full',
];

const scenarioForDate = (date: Date): DayScenario => {
  if (isWeekend(date)) {
    return {
      perProject: MOCK_PROJECTS.map(() => ({ tracked: 0, booked: 0 })),
    };
  }
  // Count weekday-only index from epoch-ish anchor (year×12 + month, day)
  const weekdayOfMonthCount = (() => {
    let count = 0;
    for (let d = 1; d <= date.getDate(); d++) {
      const probe = new Date(date.getFullYear(), date.getMonth(), d);
      if (!isWeekend(probe)) count++;
    }
    return count;
  })();
  const idx = (weekdayOfMonthCount - 1) % SCENARIO_ROTATION.length;
  return SCENARIOS[SCENARIO_ROTATION[idx]];
};

const trackedHoursForProjectDay = (scenario: DayScenario, projectIndex: number): number =>
  scenario.perProject[projectIndex]?.tracked ?? 0;

const bookedHoursForProjectDay = (scenario: DayScenario, projectIndex: number): number =>
  scenario.perProject[projectIndex]?.booked ?? 0;

const timeOffForDate = (date: Date): DayScenarioTimeOff[] => {
  if (isWeekend(date)) return [];
  return scenarioForDate(date).timeOff ?? [];
};

const isFullDayTimeOff = (entries: DayScenarioTimeOff[]): boolean =>
  entries.reduce((sum, t) => sum + t.hours, 0) >= DAILY_CAPACITY_HOURS;

const buildDayBreakdown = (date: Date, _weekStart: Date): WeekDayBreakdown => {
  const weekend = isWeekend(date);
  const timeOffEntries = timeOffForDate(date);
  const scenario = scenarioForDate(date);

  const approvedTimeOffHours = timeOffEntries
    .filter(t => t.status === TimeOffRequestSummaryStatus.APPROVED)
    .reduce((sum, t) => sum + t.hours, 0);
  const fullDayOff = isFullDayTimeOff(timeOffEntries);

  let dailyCapacity = weekend ? 0 : DAILY_CAPACITY_HOURS;
  if (approvedTimeOffHours > 0) {
    dailyCapacity = Math.max(0, dailyCapacity - approvedTimeOffHours);
  }

  const projectBreakdown: WeekDayProjectBreakdown[] =
    weekend || fullDayOff
      ? MOCK_PROJECTS.map(p => ({
          projectId: p.projectId,
          projectName: p.projectName,
          colorHex: p.colorHex,
          logoUrl: p.logoUrl,
          trackedHours: 0,
          trackedBreakdown: [],
          bookedHours: 0,
          bookedBreakdown: [],
          isBackgroundWork: false,
        }))
      : MOCK_PROJECTS.map((p, idx) => {
          const trackedHours = trackedHoursForProjectDay(scenario, idx);
          const bookedHours = bookedHoursForProjectDay(scenario, idx);
          const isBg = scenario.perProject[idx]?.isBackgroundWork === true;
          return {
            projectId: p.projectId,
            projectName: p.projectName,
            colorHex: p.colorHex,
            logoUrl: p.logoUrl,
            trackedHours,
            trackedBreakdown: trackedHours > 0 ? [{ trackedHours, isBackgroundWork: isBg }] : [],
            bookedHours,
            bookedBreakdown: bookedHours > 0 ? [{ bookedHours, isBackgroundWork: isBg }] : [],
            isBackgroundWork: isBg && bookedHours > 0,
          };
        });

  const trackedHours = projectBreakdown.reduce((sum, p) => sum + p.trackedHours, 0);
  const bookedHours = projectBreakdown.reduce((sum, p) => sum + p.bookedHours, 0);

  // Aggregate the per-project breakdowns into day-level regular vs background buckets
  // so consumers (UnifiedSummary) can derive the split without re-walking projects.
  const trackedBgHours = projectBreakdown
    .flatMap(p => p.trackedBreakdown)
    .filter(b => b.isBackgroundWork)
    .reduce((sum, b) => sum + b.trackedHours, 0);
  const trackedRegularHours = Math.max(0, trackedHours - trackedBgHours);
  const bookedBgHours = projectBreakdown
    .flatMap(p => p.bookedBreakdown)
    .filter(b => b.isBackgroundWork)
    .reduce((sum, b) => sum + b.bookedHours, 0);
  const bookedRegularHours = Math.max(0, bookedHours - bookedBgHours);

  const trackedBreakdown = [
    ...(trackedRegularHours > 0
      ? [{ trackedHours: trackedRegularHours, isBackgroundWork: false }]
      : []),
    ...(trackedBgHours > 0 ? [{ trackedHours: trackedBgHours, isBackgroundWork: true }] : []),
  ];
  const bookedBreakdown = [
    ...(bookedRegularHours > 0
      ? [{ bookedHours: bookedRegularHours, isBackgroundWork: false }]
      : []),
    ...(bookedBgHours > 0 ? [{ bookedHours: bookedBgHours, isBackgroundWork: true }] : []),
  ];

  return {
    // Encode the calendar day as UTC-midnight so consumers can read it back
    // with `getUTCDate()` without timezone drift. Matches what the backend
    // is expected to return per `WeekDayBreakdownResponse.date`.
    date: normalizeToStartOfDayUTC(date).toISOString(),
    dailyCapacityHours: dailyCapacity,
    dailyTotalCapacityHours: weekend ? 0 : DAILY_CAPACITY_HOURS,
    trackedHours,
    trackedBreakdown,
    bookedHours,
    bookedBreakdown,
    projectBreakdown,
    timeOff: timeOffEntries.map(t => ({ status: t.status, totalHours: t.hours })),
  };
};

const aggregateProjects = (days: WeekDayBreakdown[]): ProjectTimeTrackingSummary[] => {
  interface Acc {
    projectId: number;
    projectName: string;
    colorHex: string;
    logoUrl: string | null;
    trackedRegular: number;
    trackedBg: number;
    bookedRegular: number;
    bookedBg: number;
  }
  const map = new Map<number, Acc>();
  for (const day of days) {
    for (const p of day.projectBreakdown) {
      const existing = map.get(p.projectId) ?? {
        projectId: p.projectId,
        projectName: p.projectName,
        colorHex: p.colorHex,
        logoUrl: p.logoUrl,
        trackedRegular: 0,
        trackedBg: 0,
        bookedRegular: 0,
        bookedBg: 0,
      };
      for (const t of p.trackedBreakdown) {
        if (t.isBackgroundWork) existing.trackedBg += t.trackedHours;
        else existing.trackedRegular += t.trackedHours;
      }
      for (const b of p.bookedBreakdown) {
        if (b.isBackgroundWork) existing.bookedBg += b.bookedHours;
        else existing.bookedRegular += b.bookedHours;
      }
      map.set(p.projectId, existing);
    }
  }
  return Array.from(map.values()).map(a => {
    const trackedHours = a.trackedRegular + a.trackedBg;
    const bookedHours = a.bookedRegular + a.bookedBg;
    return {
      project: {
        projectId: a.projectId,
        projectName: a.projectName,
        colorHex: a.colorHex,
        logoUrl: a.logoUrl,
      },
      trackedHours,
      trackedBreakdown: [
        ...(a.trackedRegular > 0
          ? [{ trackedHours: a.trackedRegular, isBackgroundWork: false }]
          : []),
        ...(a.trackedBg > 0 ? [{ trackedHours: a.trackedBg, isBackgroundWork: true }] : []),
      ],
      bookedHours,
      bookedBreakdown: [
        ...(a.bookedRegular > 0 ? [{ bookedHours: a.bookedRegular, isBackgroundWork: false }] : []),
        ...(a.bookedBg > 0 ? [{ bookedHours: a.bookedBg, isBackgroundWork: true }] : []),
      ],
      progressPercentage: calculateProgressPercentage(trackedHours, bookedHours),
    };
  });
};

const sumTrackedBreakdowns = (days: WeekDayBreakdown[]) => {
  let regular = 0;
  let bg = 0;
  for (const d of days) {
    for (const t of d.trackedBreakdown) {
      if (t.isBackgroundWork) bg += t.trackedHours;
      else regular += t.trackedHours;
    }
  }
  return { regular, bg };
};

const sumBookedBreakdowns = (days: WeekDayBreakdown[]) => {
  let regular = 0;
  let bg = 0;
  for (const d of days) {
    for (const b of d.bookedBreakdown) {
      if (b.isBackgroundWork) bg += b.bookedHours;
      else regular += b.bookedHours;
    }
  }
  return { regular, bg };
};

const sumTimeOffByStatus = (days: WeekDayBreakdown[]): WeeklySummary['timeOffSummary'] => {
  const approved = days.reduce(
    (sum, d) =>
      sum +
      (d.timeOff.find(t => t.status === TimeOffRequestSummaryStatus.APPROVED)?.totalHours ?? 0),
    0
  );
  const pending = days.reduce(
    (sum, d) =>
      sum +
      (d.timeOff.find(t => t.status === TimeOffRequestSummaryStatus.PENDING)?.totalHours ?? 0),
    0
  );
  const result: WeeklySummary['timeOffSummary'] = [];
  if (approved > 0)
    result.push({ status: TimeOffRequestSummaryStatus.APPROVED, totalHours: approved });
  if (pending > 0)
    result.push({ status: TimeOffRequestSummaryStatus.PENDING, totalHours: pending });
  return result;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const buildWeekDays = (startDate: string, endDate: string): WeekDayBreakdown[] => {
  // Operate on UTC-midnight anchors so the day count is timezone-stable.
  // `startDate` is UTC-midnight, `endDate` is UTC-23:59:59. Compute the inclusive
  // day count by rounding the millisecond delta — date-fns' `differenceInCalendarDays`
  // uses *local* days and drifts by one in non-UTC zones, which previously
  // produced 8 entries for a 7-day window.
  const startUtc = parseISO(startDate).getTime();
  const endUtc = parseISO(endDate).getTime();
  const totalDays = Math.max(0, Math.round((endUtc - startUtc) / MS_PER_DAY)) + 1;
  const startMidnight = new Date(
    Date.UTC(
      parseISO(startDate).getUTCFullYear(),
      parseISO(startDate).getUTCMonth(),
      parseISO(startDate).getUTCDate()
    )
  );
  return Array.from({ length: totalDays }, (_, i) =>
    buildDayBreakdown(addDays(startMidnight, i), startMidnight)
  );
};

export const getMockWorklogsSummary = (startDate: string, endDate: string): WeeklySummary => {
  const days = buildWeekDays(startDate, endDate);
  const totalTrackedHours = days.reduce((s, d) => s + d.trackedHours, 0);
  const totalBookedHours = days.reduce((s, d) => s + d.bookedHours, 0);
  const totalCapacityHours = days.reduce((s, d) => s + d.dailyTotalCapacityHours, 0);
  const effectiveCapacityHours = days.reduce((s, d) => s + d.dailyCapacityHours, 0);
  const tracked = sumTrackedBreakdowns(days);
  const booked = sumBookedBreakdowns(days);
  return {
    weekStartDate: startDate,
    weekEndDate: endDate,
    totalTrackedHours,
    trackedBreakdown: [
      ...(tracked.regular > 0 ? [{ trackedHours: tracked.regular, isBackgroundWork: false }] : []),
      ...(tracked.bg > 0 ? [{ trackedHours: tracked.bg, isBackgroundWork: true }] : []),
    ],
    totalBookedHours,
    bookedBreakdown: [
      ...(booked.regular > 0 ? [{ bookedHours: booked.regular, isBackgroundWork: false }] : []),
      ...(booked.bg > 0 ? [{ bookedHours: booked.bg, isBackgroundWork: true }] : []),
    ],
    totalCapacityHours,
    effectiveCapacityHours,
    timeOffSummary: sumTimeOffByStatus(days),
    timeOffRequests: [],
    progressPercentage: calculateProgressPercentage(totalTrackedHours, totalBookedHours),
    projectSummaries: aggregateProjects(days),
  };
};

export const getMockWeekOverview = (
  startDate: string,
  endDate: string
): { summary: WeeklySummary; dailyBreakdown: WeekDayBreakdown[] } => {
  const days = buildWeekDays(startDate, endDate);
  return {
    summary: getMockWorklogsSummary(startDate, endDate),
    dailyBreakdown: days,
  };
};

export const getMockMonthOverview = (startDate: string, endDate: string): MonthOverview => {
  const days = buildWeekDays(startDate, endDate);
  const calendarDays: MonthCalendarDay[] = days.map(d => {
    const projectsWithHours = d.projectBreakdown
      .filter(p => p.trackedHours > 0)
      .sort((a, b) => b.trackedHours - a.trackedHours)
      .map(p => ({
        projectId: p.projectId,
        colorHex: p.colorHex,
      }));
    const totalTimeOffHours = d.timeOff.reduce((sum, t) => sum + t.totalHours, 0);
    return {
      date: d.date,
      trackedHours: d.trackedHours,
      totalCapacityHours: d.dailyTotalCapacityHours,
      effectiveCapacityHours: d.dailyCapacityHours,
      topProjects: projectsWithHours,
      timeOff: d.timeOff,
      isFullDayTimeOff:
        d.dailyTotalCapacityHours > 0 && totalTimeOffHours >= d.dailyTotalCapacityHours,
    };
  });

  return {
    monthStartDate: startDate,
    monthEndDate: endDate,
    days: calendarDays,
    monthlyProjects: aggregateProjects(days),
    timeOffRequests: [],
  };
};

export const getMockDayOverview = (date: string): DayOverview => {
  const day = parseISO(date);
  const dayBreakdown = buildDayBreakdown(day, day);
  const approved =
    dayBreakdown.timeOff.find(t => t.status === TimeOffRequestSummaryStatus.APPROVED)?.totalHours ??
    0;
  const pending =
    dayBreakdown.timeOff.find(t => t.status === TimeOffRequestSummaryStatus.PENDING)?.totalHours ??
    0;
  const isFullDay = approved >= DAILY_CAPACITY_HOURS;
  const timeOffStatus =
    pending > 0
      ? TimeOffRequestSummaryStatus.PENDING
      : approved > 0
        ? TimeOffRequestSummaryStatus.APPROVED
        : undefined;
  return {
    date: dayBreakdown.date,
    trackedHours: dayBreakdown.trackedHours,
    trackedBreakdown: dayBreakdown.trackedBreakdown,
    bookedHours: dayBreakdown.bookedHours,
    bookedBreakdown: dayBreakdown.bookedBreakdown,
    capacityHours: isWeekend(day) ? 0 : DAILY_CAPACITY_HOURS,
    effectiveCapacityHours: dayBreakdown.dailyCapacityHours,
    timeOffApprovedHours: approved,
    timeOffPendingHours: pending,
    isFullDayTimeOff: isFullDay,
    timeOffStatus,
    timeOff: dayBreakdown.timeOff,
    projectBreakdown: dayBreakdown.projectBreakdown,
  };
};

/**
 * Break a project's per-day tracked hours into 1-3 deterministic time entries
 * so the week-view expansion has realistic-looking content. Each entry's
 * timestamp is anchored to a deterministic hour-of-day per project so the
 * order is stable across renders.
 */
const buildEntriesForProjectDay = (
  _project: MockProject,
  date: Date,
  projectIndex: number,
  dayOffset: number,
  idBase: number
): TimeTrack[] => {
  const scenario = scenarioForDate(date);
  const totalHours = trackedHoursForProjectDay(scenario, projectIndex);
  if (totalHours <= 0) return [];

  // Split into chunks: prefer 2 chunks for >= 2h, otherwise 1.
  const chunkCount = totalHours >= 4 ? 3 : totalHours >= 2 ? 2 : 1;
  const chunkSize = Math.round((totalHours / chunkCount) * 2) / 2; // round to 0.5h
  const lastChunk = Math.round((totalHours - chunkSize * (chunkCount - 1)) * 2) / 2;

  const descriptions = [
    'Planning & sync',
    'Implementation',
    'Code review',
    'QA & polish',
    'Pair programming',
    'Bugfixes',
  ];

  const result: TimeTrack[] = [];
  for (let i = 0; i < chunkCount; i++) {
    const minutes = Math.round((i === chunkCount - 1 ? lastChunk : chunkSize) * 60);
    if (minutes <= 0) continue;
    // Anchor entries at deterministic local hours of day (9, 11, 14, 16…)
    const hourOfDay = 9 + i * 2;
    const ts = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hourOfDay, 0, 0);
    const descIdx = Math.abs((_project.projectId + dayOffset + i) % descriptions.length);
    result.push({
      id: idBase + i,
      date: ts.toISOString(),
      trackedMinutes: minutes,
      description: descriptions[descIdx],
      isRunning: false,
      isApproved: false,
    });
  }
  return result;
};

const MS_PER_DAY_LOCAL = MS_PER_DAY;

export const getMockTimeTracks = (
  startDate: string,
  endDate: string,
  projectId: number
): PaginatedResponse<TimeTrack> => {
  const projectIndex = MOCK_PROJECTS.findIndex(p => p.projectId === projectId);
  if (projectIndex < 0) {
    return new PaginatedResponse<TimeTrack>({
      items: [],
      pagination: { total: 0, cursor: null },
    });
  }
  const project = MOCK_PROJECTS[projectIndex];

  const startUtc = parseISO(startDate);
  const endUtc = parseISO(endDate);
  const totalDays =
    Math.max(0, Math.round((endUtc.getTime() - startUtc.getTime()) / MS_PER_DAY_LOCAL)) + 1;

  const startAnchor = new Date(
    startUtc.getUTCFullYear(),
    startUtc.getUTCMonth(),
    startUtc.getUTCDate()
  );

  const items: TimeTrack[] = [];
  for (let i = 0; i < totalDays; i++) {
    const date = addDays(startAnchor, i);
    if (isWeekend(date)) continue;
    // Stable id base per (project, day) — avoids collisions across days.
    const idBase = projectId * 100000 + i * 10;
    items.push(...buildEntriesForProjectDay(project, date, projectIndex, i, idBase));
  }

  return new PaginatedResponse<TimeTrack>({
    items,
    pagination: { total: items.length, cursor: null },
  });
};

export const isTimeTrackingMockEnabled = isMockEnabled;
